<template>
	<component
		:is="fieldComponentName"
		ref="baseField"
		v-model="fieldValue"
		:field="field"
		:person-number="personNumber"
		:form-data="formData"
		:additional-data="additionalData"
		:group="group"
		@update="onUpdate"
		@change="onChange"
		@focus="onFocus"
		@blur="onBlur"
	/>
</template>

<script>
/*
 * Для динамической валидации компонент из папки fields должен триггерить событие onBlur,
 * реализовывать checkValidation и содержать isValidate в data.
 */
import Email from '@/components/checkout/fields/Email.vue';
import DateField from '@/components/checkout/fields/DateField.vue';
import Phone from '@/components/checkout/fields/Phone.vue';
import StringInput from '@/components/checkout/fields/StringInput.vue';
import StringInputUploadExplicit from '@/components/checkout/fields/StringInputUploadExplicit.vue';
import City from '@/components/checkout/fields/City.vue';
import Organization from '@/components/checkout/fields/Organization.vue';
import Bank from '@/components/checkout/fields/Bank.vue';
import PassportOrg from '@/components/checkout/fields/PassportOrg.vue';
import Checkbox from '@/components/checkout/fields/Checkbox.vue';
import Dropdown from '@/components/checkout/fields/Dropdown.vue';
import UploadExplicit from '@/components/checkout/fields/UploadExplicit.vue';
import UploadAny from '@/components/checkout/fields/UploadAny.vue';
import DateUploadExplicit from '@/components/checkout/fields/DateUploadExplicit.vue';
import PassportNumberUploadExplicit from '@/components/checkout/fields/PassportNumberUploadExplicit.vue';
import AddressUploadExplicit from '@/components/checkout/fields/AddressUploadExplicit.vue';
import FieldGroup from '@/components/checkout/fields/FieldGroup.vue';
import { TYPE_TO_FIELD_MAP } from '@/components/checkout/fieldTypeDictionary';
import {
	MUTATION_SET_FORM_FIELDS_DATA,
	CHECKOUT_STORE_NAMESPACE,
	ACTION_SAVE_FORM_DATA_BACKEND,
	ACTION_SAVE_ORDER,
	MUTATION_SET_FORM_META_DATA,
} from '@/store/checkout';
import { mapActions, mapGetters } from 'vuex';
import userProfile from "~/mixins/userProfile";

export default {
	name: 'CheckoutBaseField',
	mixins: [userProfile],
	components: {
		Email,
		DateField,
		Phone,
		StringInput,
		StringInputUploadExplicit,
		Checkbox,
		Dropdown,
		City,
		Organization,
		Bank,
		UploadExplicit,
		UploadAny,
		DateUploadExplicit,
		PassportNumberUploadExplicit,
		AddressUploadExplicit,
		FieldGroup,
		PassportOrg,
	},
	props: {
		field: {
			type: Object,
			default: () => {},
		},
		personNumber: {
			type: Number,
			default: 1,
		},
		formData: {
			type: Object,
			default: () => {},
		},
		onlyType: {
			type: String,
			default: '',
		},
		group: {
			type: String,
			default: '',
		},
	},
	data() {
		return {
			formatValue: null,
		};
	},
	computed: {
		...mapGetters({
			getIsOrderExists: 'checkout/getIsOrderExists',
			getIsShowLoginPopup: 'checkout/getIsShowLoginPopup',
			getIsRegistrant: 'checkout/getIsRegistrant',
			getFieldValue: 'checkout/getFieldValue',
		}),
		fieldComponentName() {
			const type = TYPE_TO_FIELD_MAP[this.field.type]
				? TYPE_TO_FIELD_MAP[this.field.type].toLowerCase()
				: undefined;
			if (!this.onlyType || this.onlyType === type) {
				return type;
			}

			return '';
		},
		fieldValue: {
			get() {
				return this.getFieldValue({
					formId: this.formData.id,
					slug: this.field.slug,
					personNumber: this.personNumber,
				});
			},
			set(value) {
				this.$store.commit(`${CHECKOUT_STORE_NAMESPACE}/${MUTATION_SET_FORM_FIELDS_DATA}`, {
					fields: {
						[this.personNumber]: {
							[this.field.slug]: {
								value,
								personNumber: this.personNumber,
								formFieldId: this.field.form_field_id,
								additionalData: {},
							},
						},
					},
					formId: this.formData.id,
				});
			},
		},
		additionalData() {
			return this.$store.getters['checkout/getFieldAdditionalData']({
				formId: this.formData.id,
				slug: this.field.slug,
				personNumber: this.personNumber,
			});
		},
	},
	watch: {
		'$auth.user': function () {
			this.fillFieldValue();
		},
	},
	async mounted() {
		await this.fillFieldValue();
	},
	methods: {
		...mapActions({
			showLoginPopup: 'authModal/showLogin',
			showRegistrationPopup: 'authModal/showRegistration',
		}),
		onUpdate(payload) {
			if ((Object.keys(payload).length > 0 && !!payload[payload.key]) || !!payload) {
				this.formatValue = payload[payload.key];
			} else {
				this.formatValue = null;
			}
		},
		onChange() {
			this.$store.dispatch(`${CHECKOUT_STORE_NAMESPACE}/${ACTION_SAVE_FORM_DATA_BACKEND}`, {
				formId: this.formData.id,
			});

			if (this.getIsOrderExists(this.formData.id)) {
				this.$store.dispatch(`${CHECKOUT_STORE_NAMESPACE}/${ACTION_SAVE_ORDER}`, {
					formId: this.formData.id,
				});
			}
		},
		onFocus() {
			if (this.$store.state.authModal.callSourceFormId === null) {
				this.$store.commit('authModal/setCallSourceFormId', this.formData.id);
			}

			if (this.$auth.loggedIn && this.getIsShowLoginPopup(this.formData.id) !== false) {
				this.$store.commit(`${CHECKOUT_STORE_NAMESPACE}/${MUTATION_SET_FORM_META_DATA}`, {
					meta: {
						showLoginPopup: false,
					},
					formId: this.formData.id,
				});

				this.$store.dispatch(`${CHECKOUT_STORE_NAMESPACE}/${ACTION_SAVE_FORM_DATA_BACKEND}`, { formId: this.formData.id });

				return;
			}
			// if (!this.$auth.loggedIn && this.getIsShowLoginPopup(this.formData.id) !== false) {
			// 	this.showLoginPopup();
			// }
		},
		onBlur(value, isValidate, isForceBindForm) {
			const currentValue = this.formatValue ? this.formatValue : value;
			this.$emit('blur', this.field.slug, currentValue, isValidate, isForceBindForm);
		},
		checkValidation(form) {
			if (typeof this.$refs.baseField?.checkValidation === 'function') {
				return this.$refs.baseField.checkValidation(form);
			}
			return null;
		},
		getChildrenValues() {
			if (typeof this.$refs.baseField?.getChildrenValues === 'function') {
				return this.$refs.baseField.getChildrenValues();
			}
			return null;
		},
		approveValidation() {
			if (typeof this.$refs.baseField?.approveValidation === 'function') {
				this.$refs.baseField.approveValidation();
			}
			return true;
		},
		async fillFieldValue() {
			const profile = await this.getUserProfile();

			if (this.$auth.loggedIn && profile && this.personNumber === 1) {
				switch (this.field.slug) {
				case 'second_name':
					this.fieldValue = this.$auth.user.middle_name || this.getFieldValue({
						formId: this.formData.id,
						slug: this.field.slug,
						personNumber: this.personNumber,
					});
					break;
				case 'city':
					this.fieldValue = profile.location || this.getFieldValue({
						formId: this.formData.id,
						slug: this.field.slug,
						personNumber: this.personNumber,
					});
					break;
				case 'job':
					this.fieldValue = profile.job_title || this.getFieldValue({
						formId: this.formData.id,
						slug: this.field.slug,
						personNumber: this.personNumber,
					});
					break;
				case 'company':
					this.fieldValue = profile.company_name || this.getFieldValue({
						formId: this.formData.id,
						slug: this.field.slug,
						personNumber: this.personNumber,
					});
					break;
				default:
					this.fieldValue = this.$auth.user[this.field.slug] || this.getFieldValue({
						formId: this.formData.id,
						slug: this.field.slug,
						personNumber: this.personNumber,
					});
					break;
				}

				this.onChange();
				this.onBlur(this.fieldValue);
			}
		},
	},
};
</script>
